import { useState } from 'react';

interface Worksheet {
  id: string;
  title: string;
  domain: string;
  difficulty: 'Easy' | 'Medium' | 'Hard';
  pages: number;
  color: string;
}

const worksheets: Worksheet[] = [
  // Real Numbers (3)
  { id: 'rn1', title: 'Classifying Real Numbers', domain: 'Real Numbers', difficulty: 'Easy', pages: 2, color: 'bg-blue-500' },
  { id: 'rn2', title: 'Order of Operations (PEMDAS)', domain: 'Real Numbers', difficulty: 'Easy', pages: 2, color: 'bg-blue-500' },
  { id: 'rn3', title: 'Properties of Real Numbers', domain: 'Real Numbers', difficulty: 'Medium', pages: 2, color: 'bg-blue-500' },
  
  // Linear Equations (7)
  { id: 'le1', title: 'One-Step Equations', domain: 'Linear Equations', difficulty: 'Easy', pages: 2, color: 'bg-green-500' },
  { id: 'le2', title: 'Two-Step Equations', domain: 'Linear Equations', difficulty: 'Easy', pages: 2, color: 'bg-green-500' },
  { id: 'le3', title: 'Multi-Step Equations', domain: 'Linear Equations', difficulty: 'Medium', pages: 3, color: 'bg-green-500' },
  { id: 'le4', title: 'Variables on Both Sides', domain: 'Linear Equations', difficulty: 'Medium', pages: 3, color: 'bg-green-500' },
  { id: 'le5', title: 'Literal Equations', domain: 'Linear Equations', difficulty: 'Medium', pages: 2, color: 'bg-green-500' },
  { id: 'le6', title: 'Compound Inequalities', domain: 'Linear Equations', difficulty: 'Hard', pages: 3, color: 'bg-green-500' },
  { id: 'le7', title: 'Absolute Value Equations', domain: 'Linear Equations', difficulty: 'Hard', pages: 3, color: 'bg-green-500' },
  
  // Functions (6)
  { id: 'fn1', title: 'Relations and Functions', domain: 'Functions', difficulty: 'Easy', pages: 2, color: 'bg-purple-500' },
  { id: 'fn2', title: 'Domain and Range', domain: 'Functions', difficulty: 'Medium', pages: 2, color: 'bg-purple-500' },
  { id: 'fn3', title: 'Function Notation', domain: 'Functions', difficulty: 'Medium', pages: 2, color: 'bg-purple-500' },
  { id: 'fn4', title: 'Slope and Rate of Change', domain: 'Functions', difficulty: 'Medium', pages: 3, color: 'bg-purple-500' },
  { id: 'fn5', title: 'Graphing Linear Functions', domain: 'Functions', difficulty: 'Medium', pages: 3, color: 'bg-purple-500' },
  { id: 'fn6', title: 'Parallel and Perpendicular Lines', domain: 'Functions', difficulty: 'Hard', pages: 3, color: 'bg-purple-500' },
  
  // Systems (4)
  { id: 'sy1', title: 'Solving Systems by Graphing', domain: 'Systems', difficulty: 'Medium', pages: 3, color: 'bg-orange-500' },
  { id: 'sy2', title: 'Solving Systems by Substitution', domain: 'Systems', difficulty: 'Medium', pages: 3, color: 'bg-orange-500' },
  { id: 'sy3', title: 'Solving Systems by Elimination', domain: 'Systems', difficulty: 'Medium', pages: 3, color: 'bg-orange-500' },
  { id: 'sy4', title: 'Special Systems (No/Infinite Solutions)', domain: 'Systems', difficulty: 'Hard', pages: 2, color: 'bg-orange-500' },
  
  // Polynomials (7)
  { id: 'po1', title: 'Adding and Subtracting Polynomials', domain: 'Polynomials', difficulty: 'Easy', pages: 2, color: 'bg-red-500' },
  { id: 'po2', title: 'Multiplying Polynomials', domain: 'Polynomials', difficulty: 'Medium', pages: 3, color: 'bg-red-500' },
  { id: 'po3', title: 'FOIL Method', domain: 'Polynomials', difficulty: 'Medium', pages: 2, color: 'bg-red-500' },
  { id: 'po4', title: 'Factoring GCF', domain: 'Polynomials', difficulty: 'Medium', pages: 2, color: 'bg-red-500' },
  { id: 'po5', title: 'Factoring Trinomials', domain: 'Polynomials', difficulty: 'Hard', pages: 3, color: 'bg-red-500' },
  { id: 'po6', title: 'Difference of Squares', domain: 'Polynomials', difficulty: 'Medium', pages: 2, color: 'bg-red-500' },
  { id: 'po7', title: 'Perfect Square Trinomials', domain: 'Polynomials', difficulty: 'Hard', pages: 2, color: 'bg-red-500' },
  
  // Quadratics (5)
  { id: 'qu1', title: 'Solving by Factoring', domain: 'Quadratics', difficulty: 'Medium', pages: 3, color: 'bg-indigo-500' },
  { id: 'qu2', title: 'Square Root Method', domain: 'Quadratics', difficulty: 'Medium', pages: 2, color: 'bg-indigo-500' },
  { id: 'qu3', title: 'Completing the Square', domain: 'Quadratics', difficulty: 'Hard', pages: 3, color: 'bg-indigo-500' },
  { id: 'qu4', title: 'Quadratic Formula', domain: 'Quadratics', difficulty: 'Hard', pages: 3, color: 'bg-indigo-500' },
  { id: 'qu5', title: 'The Discriminant', domain: 'Quadratics', difficulty: 'Hard', pages: 2, color: 'bg-indigo-500' },
  
  // Statistics (4)
  { id: 'st1', title: 'Mean, Median, Mode', domain: 'Statistics', difficulty: 'Easy', pages: 2, color: 'bg-teal-500' },
  { id: 'st2', title: 'Box Plots and Quartiles', domain: 'Statistics', difficulty: 'Medium', pages: 2, color: 'bg-teal-500' },
  { id: 'st3', title: 'Outliers and IQR', domain: 'Statistics', difficulty: 'Medium', pages: 2, color: 'bg-teal-500' },
  { id: 'st4', title: 'Standard Deviation', domain: 'Statistics', difficulty: 'Hard', pages: 3, color: 'bg-teal-500' },
];

const Grade9StudyPlanPage = () => {
  const [selectedWeeks, setSelectedWeeks] = useState(8);

  const distributeWorksheets = (weeks: number) => {
    const schedule: { [key: number]: Worksheet[] } = {};
    const worksheetsPerWeek = Math.ceil(worksheets.length / weeks);
    
    for (let i = 0; i < weeks; i++) {
      schedule[i + 1] = worksheets.slice(i * worksheetsPerWeek, (i + 1) * worksheetsPerWeek);
    }
    
    return schedule;
  };

  const schedule = distributeWorksheets(selectedWeeks);
  const totalPages = worksheets.reduce((sum, ws) => sum + ws.pages, 0);
  const totalMinutes = totalPages * 10;
  const hoursPerWeek = Math.round((totalMinutes / selectedWeeks) / 60 * 10) / 10;

  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 print:border-0">
        <div className="max-w-7xl mx-auto px-6 py-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-4xl font-bold text-slate-900 mb-2">Grade 9 Study Plan</h1>
              <p className="text-lg text-slate-600">Personalized weekly schedule for Algebra 1 mastery</p>
            </div>
            <button
              onClick={handlePrint}
              className="flex items-center gap-2 px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors print:hidden whitespace-nowrap"
            >
              <i className="ri-printer-line text-xl"></i>
              Print Plan
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-12">
        {/* Week Selector */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-8 mb-8 print:shadow-none print:border">
          <h2 className="text-2xl font-bold text-slate-900 mb-4">Choose Your Timeline</h2>
          <p className="text-slate-600 mb-6">Select how many weeks you have to prepare. The plan will automatically distribute all {worksheets.length} worksheets.</p>
          
          <div className="flex gap-4 mb-6 print:mb-4">
            {[4, 6, 8, 12].map((weeks) => (
              <button
                key={weeks}
                onClick={() => setSelectedWeeks(weeks)}
                className={`flex-1 py-4 px-6 rounded-lg font-semibold transition-all whitespace-nowrap ${
                  selectedWeeks === weeks
                    ? 'bg-indigo-600 text-white shadow-lg scale-105'
                    : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                }`}
              >
                {weeks} Weeks
              </button>
            ))}
          </div>

          {/* Stats */}
          <div className="grid grid-cols-3 gap-6 pt-6 border-t border-slate-200">
            <div>
              <div className="text-3xl font-bold text-indigo-600">{worksheets.length}</div>
              <div className="text-sm text-slate-600">Total Worksheets</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-indigo-600">{totalPages}</div>
              <div className="text-sm text-slate-600">Total Pages</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-indigo-600">{hoursPerWeek}h</div>
              <div className="text-sm text-slate-600">Per Week</div>
            </div>
          </div>
        </div>

        {/* Weekly Schedule */}
        <div className="space-y-6 mb-8">
          {Object.entries(schedule).map(([weekNum, weekWorksheets]) => {
            if (weekWorksheets.length === 0) return null;
            
            const weekPages = weekWorksheets.reduce((sum, ws) => sum + ws.pages, 0);
            const weekMinutes = weekPages * 10;
            const focusDomain = weekWorksheets[0]?.domain || 'Mixed Topics';

            return (
              <div key={weekNum} className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden print:shadow-none print:border print:break-inside-avoid">
                {/* Week Header */}
                <div className="bg-gradient-to-r from-indigo-600 to-blue-600 px-6 py-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-2xl font-bold text-white">Week {weekNum}</h3>
                      <p className="text-indigo-100">Focus: {focusDomain}</p>
                    </div>
                    <div className="text-right">
                      <div className="text-2xl font-bold text-white">{weekWorksheets.length}</div>
                      <div className="text-sm text-indigo-100">worksheets</div>
                    </div>
                  </div>
                </div>

                {/* Worksheets List */}
                <div className="p-6">
                  <div className="space-y-3">
                    {weekWorksheets.map((worksheet, idx) => (
                      <div
                        key={worksheet.id}
                        className="flex items-center gap-4 p-4 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors"
                      >
                        <div className="flex items-center justify-center w-8 h-8 bg-indigo-100 text-indigo-700 rounded-full font-semibold text-sm flex-shrink-0">
                          {idx + 1}
                        </div>
                        
                        <div className="flex-1 min-w-0">
                          <h4 className="font-semibold text-slate-900 mb-1">{worksheet.title}</h4>
                          <div className="flex items-center gap-3 text-sm">
                            <span className={`px-2 py-1 ${worksheet.color} text-white rounded text-xs font-medium whitespace-nowrap`}>
                              {worksheet.domain}
                            </span>
                            <span className={`px-2 py-1 rounded text-xs font-medium whitespace-nowrap ${
                              worksheet.difficulty === 'Easy' ? 'bg-green-100 text-green-700' :
                              worksheet.difficulty === 'Medium' ? 'bg-yellow-100 text-yellow-700' :
                              'bg-red-100 text-red-700'
                            }`}>
                              {worksheet.difficulty}
                            </span>
                            <span className="text-slate-600">{worksheet.pages} pages</span>
                            <span className="text-slate-600">~{worksheet.pages * 10} min</span>
                          </div>
                        </div>

                        <button className="flex items-center justify-center w-10 h-10 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors flex-shrink-0 print:hidden cursor-pointer">
                          <i className="ri-download-line text-lg"></i>
                        </button>
                      </div>
                    ))}
                  </div>

                  {/* Week Summary */}
                  <div className="mt-4 pt-4 border-t border-slate-200 flex items-center justify-between text-sm">
                    <span className="text-slate-600">Week Total:</span>
                    <span className="font-semibold text-slate-900">{weekPages} pages • ~{Math.round(weekMinutes / 60 * 10) / 10} hours</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Study Tips */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-8 mb-8 print:shadow-none print:border print:break-inside-avoid">
          <h2 className="text-2xl font-bold text-slate-900 mb-4 flex items-center gap-3">
            <i className="ri-lightbulb-line text-yellow-500"></i>
            Study Tips for Grade 9 / Algebra 1
          </h2>
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <h3 className="font-semibold text-slate-900 mb-2">📝 Practice Consistently</h3>
              <p className="text-slate-600 text-sm">Complete worksheets on schedule. Consistency builds mastery faster than cramming.</p>
            </div>
            <div>
              <h3 className="font-semibold text-slate-900 mb-2">✅ Check Your Work</h3>
              <p className="text-slate-600 text-sm">Review solutions carefully. Understanding mistakes is key to improvement.</p>
            </div>
            <div>
              <h3 className="font-semibold text-slate-900 mb-2">🔄 Review Previous Topics</h3>
              <p className="text-slate-600 text-sm">Spend 10 minutes each week reviewing earlier concepts to maintain retention.</p>
            </div>
            <div>
              <h3 className="font-semibold text-slate-900 mb-2">🎯 Focus on Weak Areas</h3>
              <p className="text-slate-600 text-sm">If you struggle with a topic, do extra practice before moving forward.</p>
            </div>
            <div>
              <h3 className="font-semibold text-slate-900 mb-2">📊 Track Your Progress</h3>
              <p className="text-slate-600 text-sm">Use the progress tracker to visualize your completion and stay motivated.</p>
            </div>
            <div>
              <h3 className="font-semibold text-slate-900 mb-2">💡 Show Your Work</h3>
              <p className="text-slate-600 text-sm">Always write out steps. This helps catch errors and builds problem-solving skills.</p>
            </div>
          </div>
        </div>

        {/* Quick Links */}
        <div className="grid md:grid-cols-3 gap-6 print:hidden">
          <a
            href="/grade/9"
            className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 hover:shadow-md transition-all group cursor-pointer"
          >
            <div className="flex items-center justify-center w-12 h-12 bg-blue-100 text-blue-600 rounded-lg mb-4 group-hover:scale-110 transition-transform">
              <i className="ri-book-open-line text-2xl"></i>
            </div>
            <h3 className="font-semibold text-slate-900 mb-2">Grade 9 Curriculum</h3>
            <p className="text-sm text-slate-600">View all topics and standards</p>
          </a>

          <a
            href="/grade-9-worksheets"
            className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 hover:shadow-md transition-all group cursor-pointer"
          >
            <div className="flex items-center justify-center w-12 h-12 bg-green-100 text-green-600 rounded-lg mb-4 group-hover:scale-110 transition-transform">
              <i className="ri-file-list-3-line text-2xl"></i>
            </div>
            <h3 className="font-semibold text-slate-900 mb-2">Browse Worksheets</h3>
            <p className="text-sm text-slate-600">Filter by topic and difficulty</p>
          </a>

          <a
            href="/grade-9-progress"
            className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 hover:shadow-md transition-all group cursor-pointer"
          >
            <div className="flex items-center justify-center w-12 h-12 bg-purple-100 text-purple-600 rounded-lg mb-4 group-hover:scale-110 transition-transform">
              <i className="ri-bar-chart-box-line text-2xl"></i>
            </div>
            <h3 className="font-semibold text-slate-900 mb-2">Track Progress</h3>
            <p className="text-sm text-slate-600">Mark completed worksheets</p>
          </a>
        </div>
      </div>

      {/* Print Styles */}
      <style>{`
        @media print {
          body {
            background: white !important;
          }
          .print\\:hidden {
            display: none !important;
          }
          .print\\:border {
            border: 1px solid #e2e8f0 !important;
          }
          .print\\:shadow-none {
            box-shadow: none !important;
          }
          .print\\:break-inside-avoid {
            break-inside: avoid !important;
          }
        }
      `}</style>
    </div>
  );
};

export default Grade9StudyPlanPage;